// Data layer for Label Management (RAT-1687/1696).
import requestApi from 'utils/Request';
import {
  LABEL_NAME_CONFLICT,
  LABEL_SCOPE,
  LABELS,
  LABELS_ASSIGNABLE,
  MERCHANT,
} from 'utils/StaticApiEndpoints';

// Returns the paginated envelope: { totalPages, total, page, limit, list }.
export const listLabels = async ({
  search = '',
  regionId,
  page = 1,
  limit = 10,
  sortKey,
  sortBy,
} = {}) => {
  const params = { page, limit };
  if (search) params.search = search;
  if (regionId !== undefined && regionId !== '') params.regionId = regionId;
  if (sortKey) params.sortKey = sortKey;
  if (sortBy) params.sortBy = sortBy;
  const { data } = await requestApi.get(LABELS, { params });
  return data;
};

export const getLabelById = async (id) => {
  const { data } = await requestApi.get(`${LABELS}/${id}`);
  return data;
};

// Merchants assigned to a label, for the label detail page. Returns the
// paginated envelope: { totalPages, total, page, limit, list }.
export const getLabelMerchants = async (id, { page = 1, limit = 10 } = {}) => {
  const { data } = await requestApi.get(`${LABELS}/${id}/merchants`, {
    params: { page, limit },
  });
  return data;
};

export const createLabel = async ({ name, regionId, color }) => {
  const { data } = await requestApi.post(LABELS, { name, regionId, color });
  return data;
};

export const updateLabel = async (id, { name, color }) => {
  const { data } = await requestApi.put(`${LABELS}/${id}`, { name, color });
  return data;
};

export const deleteLabel = async (id) => {
  const { data } = await requestApi.delete(`${LABELS}/${id}`);
  return data;
};

// Name uniqueness is per-scope (RAT-1687): a Global name blocks that name in
// every region; a region name only blocks that region and is itself blocked
// by an existing Global name. Two different regions may share a name.
// Returns the clashing label, or null.
export const checkNameConflict = async (name, regionId, excludeId) => {
  if (!name?.trim()) return null;
  const { data } = await requestApi.get(LABEL_NAME_CONFLICT, {
    params: { name, regionId, excludeId },
  });
  return data;
};

// For a Global -> single-region downgrade: which assigned merchants sit
// outside the target region and would lose the label.
export const getScopeImpact = async (id, targetRegionId) => {
  const { data } = await requestApi.get(`${LABELS}/${id}/scope-impact`, {
    params: { targetRegionId },
  });
  return data;
};

// resolution: 'merge' | 'keepBoth' for an upgrade conflict; ignored on
// downgrade (targetRegionId > 0).
export const changeLabelScope = async (id, { targetRegionId, resolution }) => {
  const { data } = await requestApi.put(`${LABEL_SCOPE}/${id}`, {
    targetRegionId,
    resolution,
  });
  return data;
};

// Labels this merchant is allowed to carry: Global plus its own region, most
// used first (RAT-1689). Paginated + searchable, so the picker never loads the
// whole taxonomy. 404s when the caller may not see that merchant.
export const getAssignableLabels = async (
  merchantId,
  { search = '', page = 1, limit = 20 } = {},
) => {
  const params = { merchantId, page, limit };
  if (search) params.search = search;
  const { data } = await requestApi.get(LABELS_ASSIGNABLE, { params });
  return data;
};

// Merchants this label can be assigned to, each flagged `assigned`, for the
// picker on the label detail page.
export const getAssignableMerchants = async (
  id,
  { search = '', page = 1, limit = 10 } = {},
) => {
  const params = { page, limit };
  if (search) params.search = search;
  const { data } = await requestApi.get(
    `${LABELS}/${id}/assignable-merchants`,
    {
      params,
    },
  );
  return data;
};

// A delta, never the full set: a Dealer or Seller only sees part of a label's
// assignments, so sending "everything selected" would unassign every merchant
// they cannot see (RAT-1689).
export const assignLabelMerchants = async (id, { add = [], remove = [] }) => {
  const { data } = await requestApi.put(`${LABELS}/${id}/merchants`, {
    add,
    remove,
  });
  return data;
};

export const getMerchantLabelIds = async (merchantId) => {
  const { data } = await requestApi.get(`${MERCHANT}/${merchantId}/labels`);
  return data;
};

export const setMerchantLabels = async (merchantId, labelIds) => {
  const { data } = await requestApi.put(`${MERCHANT}/${merchantId}/labels`, {
    labelIds,
  });
  return data;
};
